import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, Optional } from '@angular/core';
import { Filter } from '@df/catalog-ui/listing/filter/filter';
import { FilterAttributeOption } from '@df/catalog-ui/listing/filter/filter-attribute-option';
import { FilterAttributeRange } from '@df/catalog-ui/listing/filter/filter-attribute-range';
import { ListingSort } from '@df/catalog-ui/listing/sort/listing-sort';
import { IModelData, Model } from '@df/catalog/model';
import { IProductDataV2 } from '@df/catalog/product/product';
import { Meta } from '@df/core/meta/meta';
import { Client } from '@df/core/service/client';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { DfModal } from '@df/ui/modal/df-modal.decorator';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { MODAL_DATA } from '@df/ui/modal/modal.service';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { Subscription } from 'rxjs';
import { Product } from '../../catalog/product/product.decorator';
import { ModalLayoutRightComponent, NModalLayoutRight } from '../modal/modal-layout-right.component';
import { FilterAttributeOptionComponent } from './filter/filter-attribute-option.component';
import { FilterAttributeRangeComponent } from './filter/filter-attribute-range.component';
import { ProductListing } from './product-listing.decorator';
import { ListingSortComponent } from './sort/listing-sort.component';

export namespace NProductListingFiltersModal {
    /**
     * Modal identifier
     */
    export const id = 'product-listing-filters';
    /**
     * Datapasset to modal
     */
    export interface IModalData {
        listing: ProductListing<Product<IProductDataV2>>;
        defaultFilter?: Filter<Product>;
    }
}
@DfModal({
    id: NProductListingFiltersModal.id,
    position: 'right',
    config: sizeName => ({
        ...NModalLayoutRight.ModalConfig,
        closeOnNavigation: false,
        width: NModalLayoutRight.getWidth(sizeName)
    }),
    reopenPolicy: NDfModal.ReopenPolicy.first,
    sizeRestriction: ['S', 'M', 'L', 'X']
})
@Component({
    templateUrl: './product-listing-filters.modal.html',
    // styleUrls: ['./filter-modal.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [GridModule, TranslationModule, ModalLayoutRightComponent, FilterAttributeRangeComponent, FilterAttributeOptionComponent, ListingSortComponent]
})
export class ProductListingFiltersModalComponent implements NDfModal.IModalComponent, OnInit {
    /**
     * Modal option config
     */
    modalConfig?: NDfModal.IModalConfig;
    /**
     * Listing changes subscription
     */
    private listingSubscription?: Subscription;

    private metaSubscription?: Subscription;

    /**
     * Listing shorthand
     */
    get listing() {
        return this.data.listing;
    }

    constructor(
        readonly client: Client,
        private changeDetectorRef: ChangeDetectorRef,
        private meta: Meta,
        @Optional() private modalRef: ModalRef<any>,
        @Optional() @Inject(MODAL_DATA) private data: NProductListingFiltersModal.IModalData
    ) {
        this.changeDetectorRef.detach();
        modalRef.afterClosed().subscribe(() => {
            this.listingSubscription?.unsubscribe();
            this.metaSubscription?.unsubscribe();
        });
        // manually not to close on facet navigation updates
        const model = meta.model;
        this.metaSubscription = meta.subscribe(() => {
            if (!model || meta.model !== model) {
                this.close();
            }
        });
    }
    /**
     * Initiate component
     */
    ngOnInit() {
        this.listingSubscription = this.listing.subscribe(() => this.detectChanges());
        this.detectChanges();
    }
    /**
     * Close modal
     */
    close() {
        this.modalRef.close();
    }
    get defaultFilter(): Filter<Product> | undefined {
        return this.data.defaultFilter;
    }
    /**
     * Change detection
     */
    private detectChanges() {
        this.changeDetectorRef.detectChanges();
    }

    asRangeFilter(filter: Filter<Product>) {
        return filter as FilterAttributeRange<Product>;
    }

    asOptionFilter(filter: Filter<Product>) {
        return filter as FilterAttributeOption<Product>
    }

    get listingSort() {
        return this.listing.sort as any as ListingSort<Model<IModelData>>
    }
}
